// Worker page section: fire drills (IF-54). Enqueue is offered only when the
// drill kind is registered here (WORKER_FIRE_DRILL_KINDS=1, OQ-14); the
// definer refuses otherwise. An assertion drill opens its own drill: alert key.
import { useState } from 'react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import { PERMS } from '../../../../rbac/permissions';
import { enqueueWorkerFireDrill, useWorkerAction, useWorkerDrills } from '../../../lib/worker-api';
import type { WorkerSectionWeb } from '../sections';
import { ActionOutcome, QueryState, SectionHeading, fmtTs, useCanOperate } from '../worker-ui';

const NAME_RE = /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+$/;
const THROW = 'system.fire-drill.throw';
const ASSERTION = 'system.fire-drill.assertion';

function DrillsSection() {
  const q = useWorkerDrills();
  const canOperate = useCanOperate();
  const [assertion, setAssertion] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const fire = useWorkerAction(enqueueWorkerFireDrill);
  const kinds = q.data?.kinds ?? [];
  const registered = (kind: string) =>
    kinds.some((k) => k.kind === kind && k.retiredAt === null && k.bootDisabledReason === null);
  const recent = q.data?.recent ?? [];
  const alerts = q.data?.alerts ?? [];
  const assertionOk = NAME_RE.test(assertion.trim());

  return (
    <section className="flex flex-col gap-3" aria-label="Drills">
      <SectionHeading
        title="Fire drills"
        description="Prove an alert route end to end. A throw drill dead-letters one job; an assertion drill injects one synthetic finding on its own drill: alert key."
      />
      <ActionOutcome error={fire.error} success={fire.error ? null : success} />
      <QueryState
        isPending={q.isPending}
        isError={q.isError}
        isEmpty={false}
        emptyTitle=""
        onRetry={() => void q.refetch()}
      >
        <div className="flex items-center gap-2 flex-wrap" aria-label="Drill kinds">
          {[THROW, ASSERTION].map((k) => (
            <Badge key={k} variant={registered(k) ? 'success' : 'neutral'}>
              {k}: {registered(k) ? 'registered' : 'not registered'}
            </Badge>
          ))}
        </div>
        {canOperate && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="secondary"
              disabled={fire.isPending || !registered(THROW)}
              onClick={() =>
                fire.mutate([{ kind: THROW }], {
                  onSuccess: (res) => setSuccess(`Throw drill enqueued: job ${res.jobId ?? '—'}`),
                })
              }
            >
              Fire throw drill
            </Button>
            <div className="basis-72">
              <Input
                type="text"
                size="sm"
                value={assertion}
                maxLength={200}
                placeholder="Assertion name"
                aria-label="Assertion to drill"
                onChange={(e) => setAssertion(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={fire.isPending || !registered(ASSERTION) || !assertionOk}
              onClick={() =>
                fire.mutate([{ kind: ASSERTION, assertion: assertion.trim() }], {
                  onSuccess: (res) =>
                    setSuccess(`Assertion drill enqueued: job ${res.jobId ?? '—'}`),
                })
              }
            >
              Fire assertion drill
            </Button>
          </div>
        )}
        <h3 className="text-body-sm-emphasis text-primary">Recent drill jobs</h3>
        {recent.length === 0 ? (
          <p className="text-body-sm text-muted">No drill jobs yet.</p>
        ) : (
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Job</TableHeaderCell>
                <TableHeaderCell>Kind</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
                <TableHeaderCell>Finished</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {recent.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>{j.id}</TableCell>
                  <TableCell>{j.kind}</TableCell>
                  <TableCell>{j.status}</TableCell>
                  <TableCell>{fmtTs(j.createdAt)}</TableCell>
                  <TableCell>{fmtTs(j.finishedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <h3 className="text-body-sm-emphasis text-primary">Drill alerts</h3>
        {alerts.length === 0 ? (
          <p className="text-body-sm text-muted">No drill alerts.</p>
        ) : (
          <ul className="text-body-sm">
            {alerts.map((a) => (
              <li key={a.key}>
                {a.key} ({a.severity}) opened {fmtTs(a.openedAt)}
                {a.resolvedAt ? `, resolved ${fmtTs(a.resolvedAt)}` : ', open'}
              </li>
            ))}
          </ul>
        )}
      </QueryState>
    </section>
  );
}

export const section: WorkerSectionWeb = {
  key: 'drills',
  order: 70,
  label: 'Drills',
  perm: PERMS.WORKER_READ,
  Component: DrillsSection,
};
